/**
 * E1 — the money that never became a book.
 *
 * `wedding_purchase_claims` (migration 446) is the buy-first Stripe Payment
 * Link path: somebody pays before they have an account and gets an emailed
 * token. NOTHING enters `wedding_purchases` — and therefore nothing enters any
 * revenue figure on this dashboard — until that token is redeemed. Until this
 * card existed, an unredeemed purchase was visible one couple at a time inside
 * a dialog and in no aggregate anywhere.
 *
 * ⚠️ EXPIRY IS LAZY AND THIS SCREEN NEVER WRITES. claimService flips a lapsed
 * claim to 'expired' the next time it looks the row up; there is no cron. The
 * server evaluates the same predicate at read time, so `summary.by_status`
 * already counts a row whose token ran out as expired even though the column
 * still says 'pending_claim'. The drill-down shows both values side by side;
 * this card shows the effective ones, which are the true ones.
 *
 * WHY THIS IS A CARD AND NOT A FIFTH KPI TILE. The Revenue KPI row is four
 * tiles at `lg=3` — a fifth would wrap one tile onto its own line at every
 * breakpoint above `sm`. It borrows WeddingKpiCard's headline typography (the
 * uppercase eyebrow, the `h4` at weight 700) so it reads as part of that row's
 * family without being in it.
 *
 * IT FETCHES ITS OWN DATA AND IGNORES THE DATE FILTER, deliberately: `summary`
 * is scoped `all_time` on the server ("unclaimed money" is not a windowed
 * question, and a tier or a date filter must not quietly shrink it). Re-running
 * it on every pill change would ask a question whose answer cannot move.
 *
 * ZERO HEX — every colour is a MUI palette key.
 */

import React from 'react';
import {
  Box, Button, Card, CardContent, Chip, Divider, Skeleton, Stack, Tooltip, Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { weddingApi, WeddingApiError } from '../weddingApi';
import type { ClaimsResponse } from '../weddingApi';
import { TIER_META, centsToUsd, centsToUsdTile, num, shortDate } from '../weddingFormat';

export const CLAIMS_PATH = '/dashboard/wedding/revenue/claims';

/** How many still-claimable rows the preview lists before deferring to the page. */
const PREVIEW_LIMIT = 4;

const ClaimsCard: React.FC = () => {
  const [data, setData] = React.useState<ClaimsResponse | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      /*
       * ONE request does both halves. The list is filtered to the effectively
       * pending rows, oldest first — the ones an operator can still rescue —
       * while `summary` and `status_counts` come back scoped to the whole
       * table regardless of that filter, which is exactly why the headline can
       * be trusted beside a filtered preview.
       */
      setData(await weddingApi.getClaims({
        status: 'pending_claim',
        sortBy: 'created_at',
        sortOrder: 'ASC',
        limit: PREVIEW_LIMIT,
      }));
    } catch (e) {
      setError(e instanceof WeddingApiError ? e.message : 'Could not load purchase claims.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const shell = (children: React.ReactNode) => (
    <Card sx={{ borderRadius: '16px', height: '100%' }} data-testid="wedding-claims-card">
      <CardContent>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700 }}
        >
          Unclaimed purchases
        </Typography>
        {children}
      </CardContent>
    </Card>
  );

  if (loading) {
    return shell(
      <>
        <Skeleton variant="text" width="55%" height={44} />
        <Skeleton variant="text" width="80%" />
      </>
    );
  }

  if (error) {
    return shell(
      <>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 1.5 }}>
          {error}
        </Typography>
        <Button size="small" variant="contained" onClick={load}>Retry</Button>
      </>
    );
  }

  /*
   * FEATURE DETECTION, NOT A ZERO. `available:false` means the table is not on
   * this database at all — the server's own sentence goes on screen and not a
   * single number does, because "we cannot see any claims" and "there are no
   * claims" are different statements and only one of them is true.
   */
  if (data && data.available === false) {
    return shell(
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {data.reason || 'Purchase claims are not reported by this database.'}
      </Typography>
    );
  }

  const summary = data ? data.summary : null;
  if (!summary) {
    return shell(
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        Purchase claims are not reported by this version of the admin API.
      </Typography>
    );
  }

  const pending = summary.by_status.pending_claim;
  const expired = summary.by_status.expired;
  const nothingOutstanding = pending.count === 0 && expired.count === 0;
  const previewRows = (data && data.claims ? data.claims : []).slice(0, PREVIEW_LIMIT);

  return shell(
    <>
      <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.2, mt: 0.5 }}>
        {centsToUsdTile(pending.cents)}
        <Typography component="span" variant="h6" color="text.secondary" sx={{ fontWeight: 600 }}>
          {` · ${pending.count} purchase${pending.count === 1 ? '' : 's'}`}
        </Typography>
      </Typography>

      {nothingOutstanding ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {summary.total_claims === 0
            ? 'Nobody has bought a book through a Payment Link yet, so there is nothing to claim.'
            : 'Every buy-first purchase has been claimed.'}
        </Typography>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {summary.oldest_pending_days === null
            ? 'Paid for and never redeemed — none of it is still claimable.'
            : `Paid for and never redeemed. The oldest has been waiting ${summary.oldest_pending_days} day${summary.oldest_pending_days === 1 ? '' : 's'}.`}
        </Typography>
      )}

      {/*
        THE AGING BUCKETS ARE PENDING-ONLY, and the server says so in
        `aging.scope`. There is no 61+ bucket because a token lives 90 days;
        the last one is open-ended downward so a clock skew cannot silently
        drop a row out of the total.
      */}
      <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
        {summary.aging.buckets.map((b) => (
          <Tooltip key={b.key} title={summary.aging.note}>
            <Chip
              size="small"
              variant="outlined"
              label={`${b.label}: ${b.count} · ${centsToUsd(b.cents)}`}
            />
          </Tooltip>
        ))}
      </Stack>

      {expired.count > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography variant="body2" color="error.main" sx={{ fontWeight: 700 }}>
              {centsToUsd(expired.cents)} in {expired.count} purchase
              {expired.count === 1 ? '' : 's'} has lapsed
            </Typography>
            <Tooltip title={data ? data.expiry_note : ''}>
              <InfoOutlinedIcon fontSize="small" color="disabled" />
            </Tooltip>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            The claim window closed and the money never became a book.
          </Typography>
        </Box>
      )}

      {previewRows.length > 0 && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
            Oldest still claimable
          </Typography>
          <Stack spacing={0.75} sx={{ mt: 0.75 }}>
            {previewRows.map((row) => (
              <Box
                key={row.id}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" noWrap>
                    {row.buyer_email || 'No buyer email recorded'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(TIER_META[row.tier] ? TIER_META[row.tier].label : row.tier)}
                    {' · '}
                    {centsToUsd(row.amount_cents)}
                    {row.claim_token_expires_at
                      ? ` · token runs out ${shortDate(row.claim_token_expires_at)}`
                      : ''}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                  {num(row.age_days)}d
                </Typography>
              </Box>
            ))}
          </Stack>
        </>
      )}

      <Box sx={{ mt: 1.5 }}>
        <Button component={RouterLink} to={CLAIMS_PATH} size="small">
          View all claims →
        </Button>
      </Box>

      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1 }}>
        All time — this figure is deliberately not scoped to the date filter above.
      </Typography>
    </>
  );
};

export default ClaimsCard;
